import React, { useState } from 'react';
import { Box, Container, Typography, styled, Stack, Chip, Button, Paper, alpha } from '@mui/material';
import Grid from '@mui/material/Grid';
import { motion, AnimatePresence } from 'framer-motion';

// Icons
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LayersIcon from '@mui/icons-material/Layers';
import SpeedIcon from '@mui/icons-material/Speed';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import CodeIcon from '@mui/icons-material/Code';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import TerminalIcon from '@mui/icons-material/Terminal';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const HybridSection = styled(Box)({
  padding: '120px 0',
  background: '#030712',
  color: '#f8fafc',
  position: 'relative',
  overflow: 'hidden',
});

const GlassCard = styled(Box)(({ theme }) => ({
  background: 'rgba(15, 23, 42, 0.65)',
  backdropFilter: 'blur(16px)',
  borderRadius: '24px',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.4)',
  position: 'relative',
  overflow: 'hidden',
  transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    borderColor: 'rgba(59, 130, 246, 0.3)',
    boxShadow: '0 25px 60px rgba(59, 130, 246, 0.12)',
  },
}));

const TechChip = styled(Chip)({
  backgroundColor: 'rgba(59, 130, 246, 0.08)',
  color: '#60a5fa',
  border: '1px solid rgba(59, 130, 246, 0.25)',
  fontWeight: 700,
  fontSize: '0.75rem',
  letterSpacing: 1.5,
  padding: '4px 8px',
  marginBottom: '20px',
  backdropFilter: 'blur(8px)'
});

const FeaturePoint = ({ text, subtext }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
    <Box sx={{ p: 0.5, borderRadius: '8px', bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', mt: 0.2 }}>
      <CheckCircleOutlineIcon sx={{ fontSize: '1.1rem' }} />
    </Box>
    <Box>
      <Typography variant="subtitle2" sx={{ color: '#f1f5f9', fontWeight: 600, fontSize: '0.925rem' }}>
        {text}
      </Typography>
      {subtext && (
        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.2, fontSize: '0.775rem' }}>
          {subtext}
        </Typography>
      )}
    </Box>
  </Stack>
);

const codeSnippets = {
  flutter: `// Flutter (Dart) High-Performance Engine
Widget build(BuildContext context) {
  return CustomScrollView(
    slivers: [
      SliverPersistentHeader(delegate: DynamicHeader()),
      SliverList(
        delegate: SliverChildBuilderDelegate(
          (context, index) => CrossPlatformTile(index),
        ),
      ),
    ],
  );
}`,
  reactNative: `// React Native (TypeScript + Reanimated)
export const DualPlatformApp = () => {
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: withSpring(1.0) }],
  }));

  return (
    <Animated.View style={[styles.card, animatedStyle]}>
      <NativeBridgeTarget platform={Platform.OS} />
    </Animated.View>
  );
}`
};

const HybridDetail = () => {
  const [selectedFramework, setSelectedFramework] = useState('flutter');

  return (
    <HybridSection id="hybrid-detail" component="section">
      {/* Background Radial Glow Effects */}
      <Box 
        sx={{ 
          position: 'absolute', top: '20%', left: '-10%', 
          width: '500px', height: '500px', 
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, transparent 70%)', 
          filter: 'blur(100px)', pointerEvents: 'none' 
        }} 
      />
      <Box 
        sx={{ 
          position: 'absolute', bottom: '10%', right: '-5%', 
          width: '600px', height: '600px', 
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)', 
          filter: 'blur(120px)', pointerEvents: 'none' 
        }} 
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          
          {/* Left Side: Architectural Context */}
          <Grid size={{ xs: 12, md: 6 }} order={{ xs: 2, md: 1 }}>
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <TechChip icon={<AutoAwesomeIcon sx={{ fontSize: '14px !important', color: '#60a5fa' }} />} label="CROSS-PLATFORM ENGINEERING" />
              
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 900, 
                  mb: 2.5, 
                  fontSize: { xs: '2.1rem', sm: '2.8rem', md: '3.2rem' }, 
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  color: '#f8fafc'
                }}
              >
                Unified Codebase.{' '}
                <Box 
                  component="span" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #ec4899 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'block'
                  }}
                >
                  Dual-Platform Velocity.
                </Box>
              </Typography>
              
              <Typography variant="body1" sx={{ color: '#94a3b8', mb: 4, fontSize: '1.05rem', lineHeight: 1.7, fontWeight: 400 }}>
                Accelerating product release cycles without sacrificing native performance. I architect cross-platform applications leveraging <b>Flutter</b> and <b>React Native</b> that render at a locked 60 FPS while sharing up to 99% of core business logic between iOS and Android.
              </Typography>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle1" sx={{ color: '#f8fafc', mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <LayersIcon sx={{ fontSize: '1.2rem', color: '#3b82f6' }} /> Architecture Stack
                  </Typography>
                  <FeaturePoint text="99% Shared Core Logic" subtext="Unified domain & network layers" />
                  <FeaturePoint text="Custom Method Channels" subtext="Direct low-level C++ & Kotlin bridges" />
                  <FeaturePoint text="Type-Safe State Mgmt" subtext="Riverpod, Redux & Zustand execution" />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle1" sx={{ color: '#f8fafc', mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <SpeedIcon sx={{ fontSize: '1.2rem', color: '#8b5cf6' }} /> Native Fluidity
                  </Typography>
                  <FeaturePoint text="60-120 FPS Rendering" subtext="Skia & Impeller GPU acceleration" />
                  <FeaturePoint text="Adaptive Platform UI" subtext="Cupertino & Material 3 adherence" />
                  <FeaturePoint text="Hot Reload Workflows" subtext="Rapid feature iteration & QA loops" />
                </Grid>
              </Grid>

              {/* Integration Banner */}
              <GlassCard sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2.5 }}>
                <Box sx={{ p: 1.5, borderRadius: '16px', background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6' }}>
                  <IntegrationInstructionsIcon sx={{ fontSize: '1.8rem' }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.975rem' }}>
                    Enterprise Integration Ready
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.85rem', mt: 0.3 }}>
                    Full compatibility with REST, GraphQL, WebSockets, offline SQLite/Room databases, and hardware peripheral SDKs.
                  </Typography>
                </Box>
              </GlassCard>
            </motion.div>
          </Grid>

          {/* Right Side: Interactive Architecture Showcase */}
          <Grid size={{ xs: 12, md: 6 }} order={{ xs: 1, md: 2 }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <GlassCard sx={{ p: 3, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                {/* Header Control Panel */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, pb: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TerminalIcon sx={{ color: '#3b82f6', fontSize: '1.3rem' }} />
                    <Typography variant="subtitle2" sx={{ color: '#e2e8f0', fontWeight: 700, fontSize: '0.875rem' }}>
                      Engine Runtime Engine
                    </Typography>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      onClick={() => setSelectedFramework('flutter')}
                      sx={{
                        py: 0.5, px: 1.5, borderRadius: '8px', fontSize: '0.75rem', fontWeight: 700,
                        textTransform: 'none',
                        bgcolor: selectedFramework === 'flutter' ? 'rgba(59, 130, 246, 0.2)' : 'transparent',
                        color: selectedFramework === 'flutter' ? '#60a5fa' : '#64748b',
                        border: '1px solid',
                        borderColor: selectedFramework === 'flutter' ? 'rgba(59, 130, 246, 0.4)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.15)' }
                      }}
                    >
                      Flutter
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setSelectedFramework('reactNative')}
                      sx={{
                        py: 0.5, px: 1.5, borderRadius: '8px', fontSize: '0.75rem', fontWeight: 700,
                        textTransform: 'none',
                        bgcolor: selectedFramework === 'reactNative' ? 'rgba(139, 92, 246, 0.2)' : 'transparent',
                        color: selectedFramework === 'reactNative' ? '#a78bfa' : '#64748b',
                        border: '1px solid',
                        borderColor: selectedFramework === 'reactNative' ? 'rgba(139, 92, 246, 0.4)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.15)' }
                      }}
                    >
                      React Native
                    </Button>
                  </Stack>
                </Box>

                {/* Live Code Snippet Display */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    bgcolor: '#090d16',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    mb: 3,
                    minHeight: '180px'
                  }}
                >
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={selectedFramework}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25 }}
                    >
                      <Typography
                        component="pre"
                        sx={{
                          fontFamily: '"Fira Code", "JetBrains Mono", monospace',
                          fontSize: '0.8rem',
                          color: selectedFramework === 'flutter' ? '#93c5fd' : '#c084fc',
                          lineHeight: 1.6,
                          overflowX: 'auto',
                          m: 0
                        }}
                      >
                        {codeSnippets[selectedFramework]}
                      </Typography>
                    </motion.div>
                  </AnimatePresence>
                </Paper>

                {/* Simultaneous OS Targets Banner */}
                <Box sx={{ bgcolor: 'rgba(2, 6, 23, 0.8)', p: 2, borderRadius: '16px', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1, display: 'block', mb: 1.5 }}>
                    PARALLEL TARGET COMPILATION
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <AppleIcon sx={{ color: '#f8fafc', fontSize: '1.4rem' }} />
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.8rem' }}>
                            iOS Binary
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600, fontSize: '0.7rem' }}>
                            ● Metal / Swift Bridge
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5, borderRadius: '12px', bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                        <AndroidIcon sx={{ color: '#34d399', fontSize: '1.4rem' }} />
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#f8fafc', fontWeight: 700, fontSize: '0.8rem' }}>
                            Android AAB
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 600, fontSize: '0.7rem' }}>
                            ● NDK / JNI Bridge
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </Box>

              </GlassCard>
            </motion.div>
          </Grid>

        </Grid>
      </Container>
    </HybridSection>
  );
};

export default HybridDetail;